/**
 * 图库页（T11b）。
 *
 * 职责：
 * - 网格展示本地图库缩略图（`galleryRepo.listGalleryImagesPage`，按生成时间倒序）；
 * - 按本地自然日分组：每天一个日期标题并另起一行，组内三列缩略图，跨天不共用行；
 * - 每张缩略图下方显示生成日期与时间（`MM-DD HH:mm`），大图预览显示完整生成时间；
 * - 点击缩略图进入大图预览（展示 prompt / 模型 / MIME / 生成时间）；
 * - 收藏 / 取消收藏、单张删除、清空图库（二次确认）、空状态引导；
 * - 每个可见部件都通过 `useComponentMeta()` 登记 390x844 逻辑坐标（安卓移植清单数据源）；
 * - 动效一律引用 `src/design/motion.ts` 的冻结预设（tap / listStagger / sheet）。
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { useMotionPresets } from '../../design/motion';
import type { MotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import {
  GRID_TOP,
  GROUP_HEADER_HEIGHT,
  PAGE_TOP,
  galleryComponentMeta,
  galleryDayCountLayout,
  galleryDayLabelLayout,
  galleryItemDeleteLayout,
  galleryItemFavoriteLayout,
  galleryItemThumbLayout,
  galleryItemTimeLayout,
  galleryLayout,
  planGalleryLayout,
} from './componentMeta';
import {
  formatGalleryDateTime,
  formatGalleryStamp,
  groupGalleryImagesByDay,
} from './grouping';
import type { GalleryDayGroup } from './grouping';
import {
  clearGallery,
  deleteGalleryImage,
  listGalleryImagesPage,
  setGalleryFavorite,
} from './galleryRepo';
import type { GalleryImage } from './galleryRepo';
import { readGalleryScope } from './galleryScope';
import { getMomentAccountId, subscribeMomentAccount } from '../moments/accountScope';
import styles from './GalleryPage.module.css';

const PAGE_TITLE = '图库';
const PAGE_SIZE = 60;
const PREVIEW_ORIGIN = { x: 16, y: 96 };
const DIALOG_ORIGIN = { x: 39, y: 320 };

function subscribeHash(listener: () => void): () => void {
  window.addEventListener('hashchange', listener);
  return () => window.removeEventListener('hashchange', listener);
}

function galleryHash(): string { return window.location.hash; }

/** 页面内绝对定位：清单坐标是画布绝对坐标，DOM 坐标相对 origin。 */
function boxStyle(
  layout: BoxLayout,
  zIndex: number,
  origin: { x: number; y: number } = { x: 0, y: PAGE_TOP },
): CSSProperties {
  return {
    position: 'absolute',
    left: layout.x - origin.x,
    top: layout.y - origin.y,
    width: layout.width,
    height: layout.height,
    zIndex,
  };
}

function labelOf(image: GalleryImage): string {
  return image.prompt.trim() === '' ? '未命名图片' : image.prompt;
}

/** 生成时间的机器可读值（本地时区的 `YYYY-MM-DDTHH:mm`）。 */
function dateTimeValue(timestamp: number): string | undefined {
  const formatted = formatGalleryDateTime(timestamp);
  return formatted === '' ? undefined : formatted.replace(' ', 'T');
}

interface GalleryDayHeaderProps {
  group: GalleryDayGroup;
  layout: BoxLayout;
  presets: MotionPresets;
}

/** 一天的日期标题：左侧完整日期 + 星期，右侧当天张数。 */
function GalleryDayHeader({ group, layout, presets }: GalleryDayHeaderProps): ReactElement {
  const base = `gallery.dateHeader.${group.key}`;
  const labelLayout = galleryDayLabelLayout(layout);
  const countLayout = galleryDayCountLayout(layout);
  const headerMeta: ComponentMetaInput = {
    id: base,
    page: 'gallery',
    role: 'container',
    label: `日期分组：${group.label}`,
    layout,
    zIndex: 5,
    props: ['date', 'count'],
    notes: '每天一行日期标题（模板坐标见 gallery.dateHeader）',
  };
  const labelMeta: ComponentMetaInput = {
    id: `${base}.label`,
    page: 'gallery',
    role: 'text',
    label: group.label,
    layout: labelLayout,
    zIndex: 6,
    props: ['date'],
    notes: '日期 + 星期；安卓用 TextView',
  };
  const countMeta: ComponentMetaInput = {
    id: `${base}.count`,
    page: 'gallery',
    role: 'text',
    label: `${group.items.length} 张图片`,
    layout: countLayout,
    zIndex: 6,
    props: ['count'],
    notes: '当天图片张数，右对齐',
  };

  const headerRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(headerMeta));
  const labelRef = useComponentMeta<HTMLHeadingElement>(buildComponentMeta(labelMeta, 'text'));
  const countRef = useComponentMeta<HTMLSpanElement>(buildComponentMeta(countMeta, 'text'));

  return (
    <motion.div
      ref={headerRef}
      className={styles.dayHeader}
      style={{
        left: layout.x,
        top: 0,
        width: layout.width,
        height: layout.height,
        zIndex: 5,
      }}
      data-ui-id={headerMeta.id}
      data-ui-role="container"
      variants={presets.listStagger.item}
    >
      <h2
        ref={labelRef}
        className={styles.dayLabel}
        style={boxStyle(labelLayout, 6, { x: layout.x, y: layout.y })}
        data-ui-id={labelMeta.id}
      >
        {group.label}
      </h2>
      <span
        ref={countRef}
        className={styles.dayCount}
        style={boxStyle(countLayout, 6, { x: layout.x, y: layout.y })}
        data-ui-id={countMeta.id}
      >
        {`${group.items.length} 张`}
      </span>
    </motion.div>
  );
}

interface GalleryItemProps {
  image: GalleryImage;
  /** 该缩略图（含下方时间条）的画布绝对布局。 */
  cell: BoxLayout;
  /** 组内图片区顶边（画布坐标），用于换算成日期分组内的相对位置。 */
  contentTop: number;
  presets: MotionPresets;
  onOpen: (image: GalleryImage) => void;
  onToggleFavorite: (image: GalleryImage) => void;
  onDelete: (image: GalleryImage) => void;
}

/** 单个网格缩略项：图片 + 生成时间条，点击看大图，角标按钮收藏 / 删除。 */
function GalleryItem({
  image,
  cell,
  contentTop,
  presets,
  onOpen,
  onToggleFavorite,
  onDelete,
}: GalleryItemProps): ReactElement {
  const base = `gallery.item.${image.id}`;
  const createdAtLabel = formatGalleryDateTime(image.createdAt);

  const cellMeta: ComponentMetaInput = {
    id: base,
    page: 'gallery',
    role: 'container',
    label: `图库图片：${labelOf(image)}`,
    layout: cell,
    zIndex: 4,
    props: ['prompt', 'model', 'createdAt', 'favorite'],
    notes: '网格项实例（模板坐标见 gallery.item）',
  };
  const thumbMeta: ComponentMetaInput = {
    id: `${base}.thumb`,
    page: 'gallery',
    role: 'button',
    label: `查看大图：${labelOf(image)}`,
    layout: galleryItemThumbLayout(cell),
    zIndex: 5,
    props: ['src'],
    notes: '点击进入大图预览',
  };
  const timeMeta: ComponentMetaInput = {
    id: `${base}.time`,
    page: 'gallery',
    role: 'text',
    label: `生成时间：${createdAtLabel}`,
    layout: galleryItemTimeLayout(cell),
    zIndex: 5,
    props: ['createdAt'],
    notes: '缩略图下方生成日期与时间；安卓用 TextView（tabular-nums）',
  };
  const favoriteMeta: ComponentMetaInput = {
    id: `${base}.favoriteButton`,
    page: 'gallery',
    role: 'button',
    label: image.favorite ? '取消收藏' : '收藏',
    layout: galleryItemFavoriteLayout(cell),
    zIndex: 6,
    props: ['favorite'],
    notes: '切换收藏状态',
  };
  const deleteMeta: ComponentMetaInput = {
    id: `${base}.deleteButton`,
    page: 'gallery',
    role: 'button',
    label: '删除图片',
    layout: galleryItemDeleteLayout(cell),
    zIndex: 6,
    notes: '删除该图（无需二次确认）',
  };

  const cellRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(cellMeta));
  const thumbRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(thumbMeta, 'button'));
  const timeRef = useComponentMeta<HTMLTimeElement>(buildComponentMeta(timeMeta, 'text'));
  const favoriteRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(favoriteMeta, 'button'));
  const deleteRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(deleteMeta, 'button'));

  return (
    <motion.li
      className={styles.cellWrap}
      style={{
        left: cell.x,
        top: cell.y - contentTop,
        width: cell.width,
        height: cell.height,
      }}
      variants={presets.listStagger.item}
    >
      <div ref={cellRef} className={styles.cell} data-ui-id={cellMeta.id} data-ui-role="container">
        <motion.button
          ref={thumbRef}
          type="button"
          className={styles.thumb}
          data-ui-id={thumbMeta.id}
          aria-label={thumbMeta.label}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onOpen(image)}
        >
          <img
            className={styles.thumbImage}
            src={image.dataUrl}
            alt={labelOf(image)}
            draggable={false}
          />
        </motion.button>
        <motion.button
          ref={favoriteRef}
          type="button"
          className={
            image.favorite ? `${styles.itemButton} ${styles.itemButtonActive}` : styles.itemButton
          }
          style={boxStyle(favoriteMeta.layout, 6, { x: cell.x, y: cell.y })}
          data-ui-id={favoriteMeta.id}
          aria-label={`${image.favorite ? '取消收藏' : '收藏'}：${labelOf(image)}`}
          aria-pressed={image.favorite}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onToggleFavorite(image)}
        >
          <span aria-hidden="true">{image.favorite ? '★' : '☆'}</span>
        </motion.button>
        <motion.button
          ref={deleteRef}
          type="button"
          className={styles.itemButton}
          style={boxStyle(deleteMeta.layout, 6, { x: cell.x, y: cell.y })}
          data-ui-id={deleteMeta.id}
          aria-label={`删除图片：${labelOf(image)}`}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onDelete(image)}
        >
          <span aria-hidden="true">🗑</span>
        </motion.button>
        <time
          ref={timeRef}
          className={styles.itemTime}
          style={boxStyle(timeMeta.layout, 5, { x: cell.x, y: cell.y })}
          data-ui-id={timeMeta.id}
          dateTime={dateTimeValue(image.createdAt)}
          title={`生成时间：${createdAtLabel}`}
        >
          {formatGalleryStamp(image.createdAt)}
        </time>
      </div>
    </motion.li>
  );
}

interface PreviewOverlayProps {
  image: GalleryImage;
  presets: MotionPresets;
  onClose: () => void;
  onToggleFavorite: (image: GalleryImage) => void;
  onDelete: (image: GalleryImage) => void;
}

/** 大图预览：完整图片 + prompt / 模型 / MIME / 生成时间 + 收藏 / 删除。 */
function PreviewOverlay({
  image,
  presets,
  onClose,
  onToggleFavorite,
  onDelete,
}: PreviewOverlayProps): ReactElement {
  const backdropRef = useComponentMeta<HTMLDivElement>(
    galleryComponentMeta('gallery.previewBackdrop'),
  );
  const cardRef = useComponentMeta<HTMLDivElement>(galleryComponentMeta('gallery.preview'));
  const imageRef = useComponentMeta<HTMLDivElement>(galleryComponentMeta('gallery.preview.image'));
  const closeRef = useComponentMeta<HTMLButtonElement>(
    galleryComponentMeta('gallery.preview.closeButton'),
  );
  const promptRef = useComponentMeta<HTMLParagraphElement>(
    galleryComponentMeta('gallery.preview.prompt'),
  );
  const metaRef = useComponentMeta<HTMLParagraphElement>(
    galleryComponentMeta('gallery.preview.meta'),
  );
  const timeRef = useComponentMeta<HTMLParagraphElement>(
    galleryComponentMeta('gallery.preview.time'),
  );
  const favoriteRef = useComponentMeta<HTMLButtonElement>(
    galleryComponentMeta('gallery.preview.favoriteButton'),
  );
  const deleteRef = useComponentMeta<HTMLButtonElement>(
    galleryComponentMeta('gallery.preview.deleteButton'),
  );
  const createdAtLabel = formatGalleryDateTime(image.createdAt);

  return (
    <>
      <motion.div
        ref={backdropRef}
        className={styles.backdrop}
        style={boxStyle(galleryLayout('gallery.previewBackdrop'), 50)}
        data-ui-id="gallery.previewBackdrop"
        data-ui-role="modal"
        variants={presets.sheetBackdrop}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      />
      <motion.div
        ref={cardRef}
        className={styles.preview}
        style={boxStyle(galleryLayout('gallery.preview'), 51)}
        data-ui-id="gallery.preview"
        data-ui-role="modal"
        role="dialog"
        aria-modal="true"
        aria-label="图片预览"
        variants={presets.sheet}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div
          ref={imageRef}
          className={styles.previewImageBox}
          style={boxStyle(galleryLayout('gallery.preview.image'), 52, PREVIEW_ORIGIN)}
          data-ui-id="gallery.preview.image"
          data-ui-role="container"
        >
          <img className={styles.previewImage} src={image.dataUrl} alt={labelOf(image)} />
        </div>
        <motion.button
          ref={closeRef}
          type="button"
          className={styles.itemButton}
          style={boxStyle(galleryLayout('gallery.preview.closeButton'), 53, PREVIEW_ORIGIN)}
          data-ui-id="gallery.preview.closeButton"
          aria-label="关闭预览"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onClose}
        >
          <span aria-hidden="true">✕</span>
        </motion.button>
        <p
          ref={promptRef}
          className={styles.previewPrompt}
          style={boxStyle(galleryLayout('gallery.preview.prompt'), 52, PREVIEW_ORIGIN)}
          data-ui-id="gallery.preview.prompt"
        >
          {image.prompt.trim() === '' ? '（无提示词）' : image.prompt}
        </p>
        <p
          ref={metaRef}
          className={styles.previewMeta}
          style={boxStyle(galleryLayout('gallery.preview.meta'), 52, PREVIEW_ORIGIN)}
          data-ui-id="gallery.preview.meta"
        >
          {`${image.model} · ${image.mime}`}
        </p>
        <p
          ref={timeRef}
          className={styles.previewTime}
          style={boxStyle(galleryLayout('gallery.preview.time'), 52, PREVIEW_ORIGIN)}
          data-ui-id="gallery.preview.time"
        >
          {`生成时间：${createdAtLabel === '' ? '未知' : createdAtLabel}`}
        </p>
        <motion.button
          ref={favoriteRef}
          type="button"
          className={styles.primaryButton}
          style={boxStyle(galleryLayout('gallery.preview.favoriteButton'), 52, PREVIEW_ORIGIN)}
          data-ui-id="gallery.preview.favoriteButton"
          aria-pressed={image.favorite}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onToggleFavorite(image)}
        >
          {image.favorite ? '取消收藏' : '收藏'}
        </motion.button>
        <motion.button
          ref={deleteRef}
          type="button"
          className={styles.dangerButton}
          style={boxStyle(galleryLayout('gallery.preview.deleteButton'), 52, PREVIEW_ORIGIN)}
          data-ui-id="gallery.preview.deleteButton"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => onDelete(image)}
        >
          删除
        </motion.button>
      </motion.div>
    </>
  );
}

interface ClearConfirmDialogProps {
  presets: MotionPresets;
  busy: boolean;
  scoped: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/** 清空图库二次确认对话框。 */
function ClearConfirmDialog({
  presets,
  busy,
  scoped,
  onCancel,
  onConfirm,
}: ClearConfirmDialogProps): ReactElement {
  const backdropRef = useComponentMeta<HTMLDivElement>(
    galleryComponentMeta('gallery.clearConfirm.backdrop'),
  );
  const dialogRef = useComponentMeta<HTMLDivElement>(
    galleryComponentMeta('gallery.clearConfirm.dialog'),
  );
  const titleRef = useComponentMeta<HTMLHeadingElement>(
    galleryComponentMeta('gallery.clearConfirm.title'),
  );
  const messageRef = useComponentMeta<HTMLParagraphElement>(
    galleryComponentMeta('gallery.clearConfirm.message'),
  );
  const cancelRef = useComponentMeta<HTMLButtonElement>(
    galleryComponentMeta('gallery.clearConfirm.cancelButton'),
  );
  const confirmRef = useComponentMeta<HTMLButtonElement>(
    galleryComponentMeta('gallery.clearConfirm.confirmButton'),
  );

  return (
    <>
      <motion.div
        ref={backdropRef}
        className={styles.backdrop}
        style={boxStyle(galleryLayout('gallery.clearConfirm.backdrop'), 60)}
        data-ui-id="gallery.clearConfirm.backdrop"
        data-ui-role="background"
        variants={presets.sheetBackdrop}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onCancel}
      />
      <motion.div
        ref={dialogRef}
        className={styles.dialog}
        style={boxStyle(galleryLayout('gallery.clearConfirm.dialog'), 61)}
        data-ui-id="gallery.clearConfirm.dialog"
        data-ui-role="modal"
        role="dialog"
        aria-modal="true"
        aria-label="清空图库确认"
        variants={presets.sheet}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <h2
          ref={titleRef}
          className={styles.dialogTitle}
          style={boxStyle(galleryLayout('gallery.clearConfirm.title'), 62, DIALOG_ORIGIN)}
          data-ui-id="gallery.clearConfirm.title"
        >
          清空图库？
        </h2>
        <p
          ref={messageRef}
          className={styles.dialogMessage}
          style={boxStyle(galleryLayout('gallery.clearConfirm.message'), 62, DIALOG_ORIGIN)}
          data-ui-id="gallery.clearConfirm.message"
        >
          {scoped ? '将删除当前图库中的图片，且无法恢复。' : '将删除本地保存的全部图片，且无法恢复。'}
        </p>
        <motion.button
          ref={cancelRef}
          type="button"
          className={styles.secondaryButton}
          style={boxStyle(galleryLayout('gallery.clearConfirm.cancelButton'), 62, DIALOG_ORIGIN)}
          data-ui-id="gallery.clearConfirm.cancelButton"
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onCancel}
        >
          取消
        </motion.button>
        <motion.button
          ref={confirmRef}
          type="button"
          className={styles.dangerButton}
          style={boxStyle(galleryLayout('gallery.clearConfirm.confirmButton'), 62, DIALOG_ORIGIN)}
          data-ui-id="gallery.clearConfirm.confirmButton"
          disabled={busy}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={onConfirm}
        >
          确认清空
        </motion.button>
      </motion.div>
    </>
  );
}

export function GalleryPage(): ReactElement {
  const presets = useMotionPresets();
  const hash = useSyncExternalStore(subscribeHash, galleryHash, () => '');
  const userId = useSyncExternalStore(subscribeMomentAccount, getMomentAccountId, () => null);
  const scope = useMemo(() => readGalleryScope(hash), [hash]);
  const scoped = scope.presetId !== undefined || scope.conversationId !== undefined || scope.empty === true;
  const scopeKey = JSON.stringify([hash, userId]);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const loaded = loadedKey === scopeKey;
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<GalleryImage | null>(null);
  const [pendingClear, setPendingClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const rootRef = useComponentMeta<HTMLElement>(galleryComponentMeta('page.gallery.root'));
  const headerRef = useComponentMeta<HTMLDivElement>(galleryComponentMeta('gallery.header'));
  const headerTitleRef = useComponentMeta<HTMLHeadingElement>(
    galleryComponentMeta('gallery.header.title'),
  );
  const clearRef = useComponentMeta<HTMLButtonElement>(galleryComponentMeta('gallery.clearButton'));
  const gridRef = useComponentMeta<HTMLDivElement>(galleryComponentMeta('gallery.grid'));
  const emptyRef = useComponentMeta<HTMLDivElement>(galleryComponentMeta('gallery.empty'));
  const emptyTitleRef = useComponentMeta<HTMLParagraphElement>(
    galleryComponentMeta('gallery.empty.title'),
  );
  const emptyHintRef = useComponentMeta<HTMLParagraphElement>(
    galleryComponentMeta('gallery.empty.hint'),
  );
  const errorBoxRef = useComponentMeta<HTMLDivElement>(
    galleryComponentMeta('gallery.errorBanner'),
  );
  const errorTextRef = useComponentMeta<HTMLParagraphElement>(
    galleryComponentMeta('gallery.errorBanner.message'),
  );

  const reload = useCallback(async (): Promise<void> => {
    const page = await listGalleryImagesPage({ ...scope, limit: PAGE_SIZE });
    setImages(page.items);
  }, [scope]);

  useEffect(() => {
    let alive = true;
    setImages([]);
    setPreview(null);
    setPendingClear(false);
    void (async () => {
      try {
        const page = await listGalleryImagesPage({ ...scope, limit: PAGE_SIZE });
        if (!alive) return;
        setImages(page.items);
        setError(null);
      } catch {
        if (alive) setError('读取本地图库失败');
      } finally {
        if (alive) setLoadedKey(scopeKey);
      }
    })();
    return () => {
      alive = false;
    };
  }, [scope, scopeKey]);

  const applyFavorite = useCallback((id: string, favorite: boolean): void => {
    setImages((prev) => prev.map((item) => (item.id === id ? { ...item, favorite } : item)));
    setPreview((prev) => (prev !== null && prev.id === id ? { ...prev, favorite } : prev));
  }, []);

  const toggleFavorite = useCallback(
    (image: GalleryImage): void => {
      const next = !image.favorite;
      applyFavorite(image.id, next);
      void (async () => {
        try {
          const updated = await setGalleryFavorite(image.id, next);
          if (updated === undefined) setError('图片已不存在');
          else setError(null);
        } catch {
          applyFavorite(image.id, !next);
          setError('更新收藏状态失败');
        }
      })();
    },
    [applyFavorite],
  );

  const removeImage = useCallback(
    (image: GalleryImage): void => {
      setImages((prev) => prev.filter((item) => item.id !== image.id));
      setPreview((prev) => (prev !== null && prev.id === image.id ? null : prev));
      void (async () => {
        try {
          await deleteGalleryImage(image.id);
          setError(null);
        } catch {
          setError('删除图片失败');
          try {
            await reload();
          } catch {
            // 读取失败时保留错误提示即可。
          }
        }
      })();
    },
    [reload],
  );

  const confirmClear = useCallback((): void => {
    setClearing(true);
    void (async () => {
      try {
        await clearGallery(scope);
        setImages([]);
        setPreview(null);
        setPendingClear(false);
        setError(null);
      } catch {
        setError('清空图库失败');
      } finally {
        setClearing(false);
      }
    })();
  }, [scope]);

  const groups = groupGalleryImagesByDay(loaded ? images : []);
  const plan = planGalleryLayout(groups.map((group) => group.items.length));

  return (
    <section
      ref={rootRef}
      className={styles.page}
      data-ui-id="page.gallery.root"
      data-ui-role="container"
      data-skin-surface="page"
    >
      <div
        ref={headerRef}
        className={styles.header}
        style={boxStyle(galleryLayout('gallery.header'), 3)}
        data-ui-id="gallery.header"
        data-ui-role="container"
      >
        <h1
          ref={headerTitleRef}
          className={styles.title}
          style={boxStyle(galleryLayout('gallery.header.title'), 4)}
          data-ui-id="gallery.header.title"
        >
          {scoped ? '角色图库' : PAGE_TITLE}
        </h1>
        <motion.button
          ref={clearRef}
          type="button"
          className={styles.clearButton}
          style={boxStyle(galleryLayout('gallery.clearButton'), 4)}
          data-ui-id="gallery.clearButton"
          disabled={!loaded || images.length === 0}
          whileTap={presets.tap.whileTap}
          transition={presets.tap.transition}
          onClick={() => setPendingClear(true)}
        >
          清空图库
        </motion.button>
      </div>

      <div
        ref={gridRef}
        className={styles.gridBox}
        style={boxStyle(galleryLayout('gallery.grid'), 3)}
        data-ui-id="gallery.grid"
        data-ui-role="list"
        role="group"
        aria-label="图库"
      >
        <div className={styles.grid} style={{ height: plan.height }}>
          {groups.map((group, groupIndex) => {
            const layout = plan.groups[groupIndex];
            if (layout === undefined) return null;
            const contentTop = layout.top + GROUP_HEADER_HEIGHT;
            return (
              <section
                key={group.key}
                className={styles.dayGroup}
                style={{ top: layout.top - GRID_TOP, height: layout.height }}
                aria-label={`${group.label}，${group.items.length} 张图片`}
              >
                <GalleryDayHeader group={group} layout={layout.header} presets={presets} />
                <motion.ul
                  className={styles.dayItems}
                  style={{ top: GROUP_HEADER_HEIGHT, height: layout.height - GROUP_HEADER_HEIGHT }}
                  aria-label={`${group.label}的图片`}
                  variants={presets.listStagger.container}
                  initial="hidden"
                  animate="visible"
                >
                  {group.items.map((image, index) => {
                    const cell = layout.items[index];
                    if (cell === undefined) return null;
                    return (
                      <GalleryItem
                        key={image.id}
                        image={image}
                        cell={cell}
                        contentTop={contentTop}
                        presets={presets}
                        onOpen={(target) => setPreview(target)}
                        onToggleFavorite={toggleFavorite}
                        onDelete={removeImage}
                      />
                    );
                  })}
                </motion.ul>
              </section>
            );
          })}
        </div>
      </div>

      {loaded && images.length === 0 ? (
        <div
          ref={emptyRef}
          className={styles.empty}
          style={boxStyle(galleryLayout('gallery.empty'), 3)}
          data-ui-id="gallery.empty"
          data-ui-role="container"
        >
          <p
            ref={emptyTitleRef}
            className={styles.emptyTitle}
            style={boxStyle(galleryLayout('gallery.empty.title'), 4)}
            data-ui-id="gallery.empty.title"
          >
            图库还是空的
          </p>
          <p
            ref={emptyHintRef}
            className={styles.emptyHint}
            style={boxStyle(galleryLayout('gallery.empty.hint'), 4)}
            data-ui-id="gallery.empty.hint"
          >
            生成图片后会自动保存到这里；点缩略图即可查看大图、收藏或删除。
          </p>
        </div>
      ) : null}

      {error !== null ? (
        <>
          <div
            ref={errorBoxRef}
            className={styles.errorBanner}
            style={boxStyle(galleryLayout('gallery.errorBanner'), 40)}
            data-ui-id="gallery.errorBanner"
            data-ui-role="container"
          />
          <p
            ref={errorTextRef}
            className={styles.errorText}
            style={boxStyle(galleryLayout('gallery.errorBanner.message'), 41)}
            data-ui-id="gallery.errorBanner.message"
            role="alert"
          >
            {error}
          </p>
        </>
      ) : null}

      <AnimatePresence>
        {loaded && preview !== null ? (
          <PreviewOverlay
            key="preview"
            image={preview}
            presets={presets}
            onClose={() => setPreview(null)}
            onToggleFavorite={toggleFavorite}
            onDelete={removeImage}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {loaded && pendingClear ? (
          <ClearConfirmDialog
            key="clear"
            presets={presets}
            busy={clearing}
            scoped={scoped}
            onCancel={() => setPendingClear(false)}
            onConfirm={confirmClear}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
}
